import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useAppSelector } from '../../../hooks/useAppSelector';
import * as clientSlice from '../../../data/client.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import {
  Reservation,
  TripLeg,
  SyncedTripLeg,
  TripLegStatusLogEntry,
} from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import TripLegDetails from './TripLegDetails';
import TripLegStatusStepper from './TripLegStatusStepper';
import TripLegLiveTracking from './TripLegLiveTracking';
import TripLegActivity from './TripLegActivity';
import { Passenger } from '../../../data/models/passenger.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

const PREFIX = 'ReservationDetails';

const classes = {
  container: `${PREFIX}-container`,
  tabDetailsContainer: `${PREFIX}-tab-details-container`,
  detailsLeft: `${PREFIX}-details-left`,
  detailsRight: `${PREFIX}-details-right`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.tabDetailsContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  [`& .${classes.detailsLeft}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      width: '384px',
    },
  },
  [`& .${classes.detailsRight}`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(3),
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      width: 'auto',
    },
  },
}));

interface ReservationDetailsProps {
  passenger?: Passenger | null;
  reservation?: Reservation;
  syncedTripLegs: {
    [id: string]: SyncedTripLeg;
  };
  legIndex: number;
  setLegIndex: Dispatch<SetStateAction<number>>;
}

const ReservationDetails = ({
  passenger,
  reservation,
  syncedTripLegs,
  legIndex,
  setLegIndex,
}: ReservationDetailsProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const clientData = useAppSelector(clientSlice.selectPassengerClientData);
  const reservationTripLegs = useAppSelector(
    tripLegSlice.selectReservationTripLegs,
  );

  useEffect(() => {
    if (reservation) {
      const tripLegIDs = reservation.tripLegs.map(
        (tl) => tl.tripLegReferenceID!,
      );
      dispatch(
        tripLegSlice.fetchReservationTripLegsAsync(
          reservation.reservationReferenceID!,
          tripLegIDs,
        ),
      );
    } else {
      dispatch(tripLegSlice.setReservationTripLegs(null));
      dispatch(tripLegSlice.setLoadedReservationTripLegs(false));
    }
  }, [reservation]);

  const tripLeg = useMemo(
    () => reservation?.tripLegs[legIndex],
    [reservation, legIndex],
  );
  const syncedTripLeg = useMemo(() => {
    let stl = null;
    if (syncedTripLegs && tripLeg?.tripLegReferenceID) {
      stl = syncedTripLegs[tripLeg.tripLegReferenceID] || null;
    }
    return stl;
  }, [syncedTripLegs, tripLeg]);

  const tripLegLogs = useMemo(() => {
    let logs: TripLegStatusLogEntry[] = [];
    if (reservationTripLegs && tripLeg?.tripLegReferenceID) {
      const leg = reservationTripLegs.tripLegs.find(
        (tl) => tl.tripLegReferenceID === tripLeg.tripLegReferenceID,
      );
      if (leg) {
        logs = leg.tripLegStatusLogs || [];
      }
    }
    return logs;
  }, [reservationTripLegs, tripLeg]);

  const tripLegPurpose = useMemo(() => {
    const pc = clientData?.purposeCodes?.find(
      (p) => p.purposeCodeUUID === tripLeg?.purposeCodeUUID,
    );
    return pc?.name || '';
  }, [tripLeg, clientData]);

  const handleLegIndexChange = (
    _evt: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    setLegIndex(newValue);
  };

  const isCurrentTripLeg = (tripLeg: TripLeg) => {
    return (
      tripLeg.status === TripLegStatus.Confirmed ||
      tripLeg.status === TripLegStatus.InTransit ||
      tripLeg.status === TripLegStatus.Onboard ||
      tripLeg.status === TripLegStatus.Onsite
    );
  };

  return (
    <StyledBox className={classes.container}>
      {reservation && (
        <>
          <Tabs
            variant="standard"
            indicatorColor="primary"
            textColor="primary"
            value={legIndex}
            onChange={handleLegIndexChange}
            sx={{ px: 2 }}>
            {reservation.tripLegs.map((leg, idx) => (
              <Tab
                key={idx}
                label={
                  t('reservation.leg', { leg: idx + 1 }) +
                  (isCurrentTripLeg(leg)
                    ? ` (${t('reservation.current')})`
                    : '')
                }
              />
            ))}
          </Tabs>
        </>
      )}

      {tripLeg && passenger && reservation && (
        <>
          <Box className={classes.tabDetailsContainer}>
            <Box className={classes.detailsLeft}>
              <TripLegDetails
                tripLeg={tripLeg}
                tripLegPurpose={tripLegPurpose}
                syncedTripLeg={syncedTripLeg}
                isOnlyTripLeg={reservation.tripLegs.length === 1}
                passenger={passenger}
              />

              <TripLegActivity
                tripLeg={tripLeg}
                syncedTripLeg={syncedTripLeg}
              />
            </Box>
            <Box className={classes.detailsRight}>
              {syncedTripLeg && (
                <TripLegStatusStepper
                  tripLeg={tripLeg}
                  statusLogs={tripLegLogs}
                />
              )}
              <TripLegLiveTracking tripLeg={tripLeg} />
            </Box>
          </Box>
        </>
      )}
    </StyledBox>
  );
};

export default ReservationDetails;
